<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import DataQualityGovernanceAPI from 'dashboard/api/dataQualityGovernance';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import {
  FRAUD_OUTCOME_OPTIONS,
  GOVERNANCE_QUEUE_FILTERS,
  QUARANTINE_SCOPE_OPTIONS,
  entriesForDisplay,
  formatBackendError,
  normalizeGovernancePayload,
} from '../utils/dataQualityGovernance';

const account = useAccount();
const { checkPermissions } = usePolicy();

const governance = ref(normalizeGovernancePayload({ data: {} }));
const selectedKey = ref('');
const queueFilter = ref('all');
const reviewReason = ref('');
const fraudOutcome = ref('confirmed');
const quarantineScope = ref('listing');
const includeEvidence = ref(false);
const isLoading = ref(false);
const actionKey = ref('');
const loadError = ref('');
const actionMessage = ref('');
const conflictMessage = ref('');

const canManageGovernance = computed(() =>
  checkPermissions(['administrator', 'data_quality_manage'])
);

const summaryCards = computed(() => [
  {
    key: 'issues',
    label: 'Issues abertas',
    value: governance.value.summary.issues.openCount,
    tone: governance.value.summary.issues.openCount ? 'warning' : 'positive',
  },
  {
    key: 'duplicates',
    label: 'Duplicidades em revisao',
    value: governance.value.summary.duplicateCandidates.reviewRequiredCount,
    tone: governance.value.summary.duplicateCandidates.reviewRequiredCount
      ? 'warning'
      : 'positive',
  },
  {
    key: 'fraud',
    label: 'Fraude exige humano',
    value: governance.value.summary.fraudSignals.reviewRequiredCount,
    tone: governance.value.summary.fraudSignals.reviewRequiredCount
      ? 'danger'
      : 'positive',
  },
  {
    key: 'alerts',
    label: 'Alertas vinculados',
    value: governance.value.summary.linkedAlerts.linkedToConsoleCount,
    tone: governance.value.summary.linkedAlerts.linkedToConsoleCount
      ? 'info'
      : 'neutral',
  },
]);

const queueItems = computed(() => {
  const items = governance.value.queueItems;
  if (queueFilter.value === 'all') return items;

  const kindByFilter = {
    issues: 'issue',
    duplicates: 'duplicate',
    fraud: 'fraud',
    scores: 'score',
  };
  return items.filter(item => item.kind === kindByFilter[queueFilter.value]);
});

const selectedItem = computed(
  () =>
    queueItems.value.find(item => item.key === selectedKey.value) ||
    queueItems.value[0] ||
    null
);

const selectedIssue = computed(() =>
  selectedItem.value?.kind === 'issue' ? selectedItem.value : null
);
const selectedDuplicate = computed(() =>
  selectedItem.value?.kind === 'duplicate' ? selectedItem.value : null
);
const selectedFraud = computed(() =>
  selectedItem.value?.kind === 'fraud' ? selectedItem.value : null
);
const selectedScore = computed(() =>
  selectedItem.value?.kind === 'score' ? selectedItem.value : null
);
const activeOperation = computed(
  () => selectedDuplicate.value?.latestMergeOperation || null
);
const reasonMissing = computed(() => !reviewReason.value.trim());

const imovelDuplicatesRoute = computed(() =>
  account.accountScopedRoute('imoveis_duplicate_candidates')
);

const statusTone = value =>
  ({
    open: 'warning',
    reopened: 'danger',
    acknowledged: 'info',
    resolved: 'positive',
    dismissed: 'neutral',
    pending: 'warning',
    confirmed: 'info',
    merged: 'positive',
    rejected: 'neutral',
    flagged: 'warning',
    quarantined: 'danger',
    cleared: 'positive',
    planned: 'info',
    previewed: 'info',
    applied: 'positive',
    reverted: 'neutral',
    critical: 'danger',
    warning: 'warning',
    info: 'info',
  })[String(value || '').toLowerCase()] || 'neutral';

const humanize = value =>
  String(value || '-')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const itemKindLabel = item =>
  ({
    issue: 'Qualidade',
    duplicate: 'Duplicidade',
    fraud: 'Fraude',
    score: 'Score',
  })[item?.kind] || 'Item';

const itemStatus = item => {
  if (!item) return '';
  return item.status || item.disposition || item.scoreKind || '';
};

const itemTitle = item => {
  if (!item) return 'Selecione um item';
  if (item.kind === 'duplicate') {
    return `${item.primaryEntity.label} x ${item.duplicateEntity.label}`;
  }
  if (item.kind === 'fraud') {
    return `${item.entity.label} · ${humanize(item.signalType)}`;
  }
  if (item.kind === 'score') {
    return `${item.entity.label} · ${humanize(item.scoreKind)}`;
  }
  return `${item.entity.label} · ${humanize(item.issueType)}`;
};

const itemSubtitle = item => {
  if (!item) return '';
  if (item.kind === 'duplicate') {
    return `${item.entityType} · ${humanize(item.matchType)} · ${item.matchScoreLabel}`;
  }
  if (item.kind === 'fraud') {
    return `${item.entityType} · ${humanize(item.severity)} · ${formatDate(item.detectedAt)}`;
  }
  if (item.kind === 'score') {
    return `${item.entityType} · ${item.compositeScoreLabel}`;
  }
  return `${item.entityType} · ${humanize(item.severity)} · ${formatDate(item.updatedAt)}`;
};

const selectItem = item => {
  selectedKey.value = item.key;
  actionMessage.value = '';
  conflictMessage.value = '';
};

const syncSelection = () => {
  if (!queueItems.value.length) {
    selectedKey.value = '';
    return;
  }

  if (!queueItems.value.some(item => item.key === selectedKey.value)) {
    selectedKey.value = queueItems.value[0].key;
  }
};

const fetchGovernance = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await DataQualityGovernanceAPI.get({
      include_evidence: includeEvidence.value,
      limit: 50,
    });
    governance.value = normalizeGovernancePayload(data);
    syncSelection();
  } catch {
    loadError.value = 'Nao foi possivel carregar a governanca de qualidade.';
  } finally {
    isLoading.value = false;
  }
};

const ensureReason = () => {
  if (!reasonMissing.value) return true;

  actionMessage.value = 'Informe o motivo da decisao humana antes de gravar.';
  return false;
};

const handleActionError = error => {
  const details = formatBackendError(error);
  const prefix = details.conflict ? 'Conflito 409 preservado: ' : '';
  conflictMessage.value = `${prefix}${details.message}`;
  useAlert(conflictMessage.value);
};

const runAction = async (key, callback, successMessage) => {
  actionKey.value = key;
  actionMessage.value = '';
  conflictMessage.value = '';
  try {
    await callback();
    actionMessage.value = successMessage;
    useAlert(successMessage);
    await fetchGovernance();
  } catch (error) {
    handleActionError(error);
  } finally {
    actionKey.value = '';
  }
};

const issuePayload = () => ({
  reason: reviewReason.value.trim(),
  note: reviewReason.value.trim(),
  resolution: reviewReason.value.trim(),
});

const transitionIssue = action => {
  if (!selectedIssue.value) return;
  if (['resolve', 'dismiss', 'reopen', 'reconcile'].includes(action)) {
    if (!ensureReason()) return;
  }

  runAction(
    `issue:${action}:${selectedIssue.value.id}`,
    () =>
      DataQualityGovernanceAPI.transitionIssue(
        selectedIssue.value.id,
        action,
        issuePayload()
      ),
    `Issue ${humanize(action).toLowerCase()} registrada.`
  );
};

const planMerge = async ({ refresh = true } = {}) => {
  if (!selectedDuplicate.value || !ensureReason()) return null;

  const request = () =>
    DataQualityGovernanceAPI.planMerge({
      duplicate_candidate_id: selectedDuplicate.value.id,
      reason: reviewReason.value.trim(),
    });

  if (!refresh) {
    const { data } = await request();
    return data?.data;
  }

  await runAction(
    `plan:${selectedDuplicate.value.id}`,
    request,
    'Plano de merge reversivel criado.'
  );
  return null;
};

const applyMerge = async () => {
  if (!selectedDuplicate.value || !ensureReason()) return;

  await runAction(
    `apply:${selectedDuplicate.value.id}`,
    async () => {
      const operation =
        activeOperation.value?.id &&
        ['planned', 'previewed'].includes(activeOperation.value.status)
          ? activeOperation.value
          : await planMerge({ refresh: false });

      if (!operation?.id) return;
      await DataQualityGovernanceAPI.applyMerge({
        merge_operation_id: operation.id,
      });
    },
    'Merge aplicado com aprovacao humana.'
  );
};

const revertMerge = async () => {
  if (!activeOperation.value?.id || !ensureReason()) return;

  await runAction(
    `revert:${activeOperation.value.id}`,
    () =>
      DataQualityGovernanceAPI.revertMerge({
        merge_operation_id: activeOperation.value.id,
        reason: reviewReason.value.trim(),
      }),
    'Merge revertido com motivo registrado.'
  );
};

const rejectDuplicate = () => {
  if (!selectedDuplicate.value || !ensureReason()) return;

  runAction(
    `reject:${selectedDuplicate.value.id}`,
    () =>
      DataQualityGovernanceAPI.rejectDuplicateCandidate({
        duplicate_candidate_id: selectedDuplicate.value.id,
        reason: reviewReason.value.trim(),
      }),
    'Candidato marcado como falso positivo.'
  );
};

const reviewFraud = () => {
  if (!selectedFraud.value || !ensureReason()) return;

  runAction(
    `fraud-review:${selectedFraud.value.id}`,
    () =>
      DataQualityGovernanceAPI.reviewFraudSignal({
        fraud_signal_id: selectedFraud.value.id,
        outcome: fraudOutcome.value,
        reason: reviewReason.value.trim(),
      }),
    'Sinal de fraude revisado.'
  );
};

const quarantineFraud = () => {
  if (!selectedFraud.value || !ensureReason()) return;

  runAction(
    `fraud-quarantine:${selectedFraud.value.id}`,
    () =>
      DataQualityGovernanceAPI.quarantineFraudSignal({
        fraud_signal_id: selectedFraud.value.id,
        scope: quarantineScope.value,
        reason: reviewReason.value.trim(),
      }),
    'Sinal enviado para quarentena reversivel.'
  );
};

const releaseFraud = () => {
  if (!selectedFraud.value || !ensureReason()) return;

  runAction(
    `fraud-release:${selectedFraud.value.id}`,
    () =>
      DataQualityGovernanceAPI.releaseFraudSignal({
        fraud_signal_id: selectedFraud.value.id,
        reason: reviewReason.value.trim(),
      }),
    'Quarentena liberada com revisao humana.'
  );
};

watch(queueFilter, syncSelection);
watch(includeEvidence, fetchGovernance);
onMounted(fetchGovernance);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main class="flex h-full min-h-0 flex-col overflow-auto bg-n-background">
    <LaisHeader
      title="Governanca de qualidade e fraude"
      subtitle="Console consolidado para duplicidades, sinais, scores, alertas e decisoes humanas reversiveis."
    />

    <section class="border-b border-n-weak px-4 py-4 sm:px-6">
      <div
        class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between"
      >
        <div class="grid gap-3 sm:grid-cols-[minmax(180px,240px)_auto]">
          <label class="flex min-w-0 flex-col text-xs text-n-slate-11">
            Fila
            <select
              v-model="queueFilter"
              class="mt-1 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
              data-test="data-quality-governance-filter"
            >
              <option
                v-for="option in GOVERNANCE_QUEUE_FILTERS"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
          <label class="mt-6 flex items-center gap-2 text-sm text-n-slate-11">
            <input
              v-model="includeEvidence"
              type="checkbox"
              class="size-4 rounded border-n-weak"
            />
            Incluir evidencia bruta
          </label>
        </div>
        <div class="flex flex-col gap-2 sm:flex-row">
          <RouterLink :to="imovelDuplicatesRoute">
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-git-compare-arrows"
            >
              Fila de imoveis
            </LaisPrimaryButton>
          </RouterLink>
          <LaisPrimaryButton
            icon="i-lucide-refresh-cw"
            :is-loading="isLoading"
            @click="fetchGovernance"
          >
            Atualizar
          </LaisPrimaryButton>
        </div>
      </div>
    </section>

    <section class="grid gap-3 px-4 py-4 sm:px-6 md:grid-cols-4">
      <LaisCard v-for="card in summaryCards" :key="card.key" class="p-4">
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-xs text-n-slate-11">{{ card.label }}</p>
            <p class="mt-1 text-2xl font-semibold text-n-slate-12">
              {{ card.value }}
            </p>
          </div>
          <LaisStatusBadge :label="humanize(card.tone)" :tone="card.tone" dot />
        </div>
      </LaisCard>
    </section>

    <section
      v-if="!canManageGovernance"
      class="mx-4 mb-4 rounded-lg border border-n-amber-7 bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11 sm:mx-6"
      role="status"
      data-test="data-quality-governance-permission"
    >
      Voce pode auditar a fila, mas decisoes de merge, fraude e resolucao exigem
      administrador ou permissao data_quality_manage.
    </section>

    <section
      class="grid min-h-0 flex-1 gap-4 px-4 pb-6 sm:px-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(420px,1.05fr)]"
    >
      <div class="min-h-0 min-w-0 overflow-hidden">
        <p
          v-if="loadError"
          class="mb-3 text-sm text-n-ruby-11"
          role="alert"
          data-test="data-quality-governance-error"
        >
          {{ loadError }}
        </p>
        <div
          v-if="isLoading"
          class="py-10 text-center text-sm text-n-slate-11"
          aria-live="polite"
        >
          Carregando governanca...
        </div>
        <div
          v-else-if="queueItems.length === 0"
          class="py-10 text-center text-sm text-n-slate-11"
          data-test="data-quality-governance-empty"
        >
          Nenhum item encontrado para este filtro.
        </div>
        <div
          v-else
          class="flex flex-col gap-3"
          data-test="data-quality-governance-queue"
        >
          <button
            v-for="item in queueItems"
            :key="item.key"
            type="button"
            class="rounded-lg border bg-n-background p-4 text-left transition hover:border-n-brand"
            :class="
              selectedItem?.key === item.key
                ? 'border-n-brand shadow-sm'
                : 'border-n-weak'
            "
            @click="selectItem(item)"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <LaisStatusBadge
                    :label="itemKindLabel(item)"
                    tone="info"
                    size="sm"
                    dot
                  />
                  <LaisStatusBadge
                    :label="humanize(itemStatus(item))"
                    :tone="statusTone(itemStatus(item))"
                    size="sm"
                    dot
                  />
                  <LaisStatusBadge
                    v-if="item.humanApproval?.required || item.requiresReview"
                    label="Aprovacao humana"
                    tone="warning"
                    size="sm"
                    icon="i-lucide-user-check"
                  />
                </div>
                <p
                  class="mt-2 break-words text-sm font-semibold text-n-slate-12"
                >
                  {{ itemTitle(item) }}
                </p>
                <p class="mt-1 break-words text-xs text-n-slate-11">
                  {{ itemSubtitle(item) }}
                </p>
                <p
                  v-if="item.recommendation"
                  class="mt-2 break-words text-xs text-n-slate-11"
                >
                  {{ item.recommendation }}
                </p>
              </div>
              <span
                class="i-lucide-chevron-right mt-1 size-4 shrink-0 text-n-slate-10"
              />
            </div>
          </button>
        </div>
      </div>

      <aside class="min-w-0">
        <LaisCard class="p-4" data-test="data-quality-governance-detail">
          <template v-if="selectedItem">
            <div class="flex flex-col gap-3 border-b border-n-weak pb-4">
              <div class="flex flex-wrap items-center gap-2">
                <LaisStatusBadge
                  :label="itemKindLabel(selectedItem)"
                  tone="info"
                  dot
                />
                <LaisStatusBadge
                  :label="humanize(itemStatus(selectedItem))"
                  :tone="statusTone(itemStatus(selectedItem))"
                  dot
                />
              </div>
              <div>
                <h2 class="break-words text-lg font-semibold text-n-slate-12">
                  {{ itemTitle(selectedItem) }}
                </h2>
                <p class="mt-1 break-words text-sm text-n-slate-11">
                  {{ itemSubtitle(selectedItem) }}
                </p>
              </div>
              <p
                v-if="selectedItem.recommendation"
                class="rounded-lg border border-n-blue-6 bg-n-blue-3 px-3 py-2 text-sm text-n-blue-11"
              >
                {{ selectedItem.recommendation }}
              </p>
            </div>

            <div class="grid gap-3 py-4 sm:grid-cols-2">
              <div
                v-if="selectedDuplicate"
                class="rounded-lg border border-n-weak p-3"
              >
                <p class="text-xs font-semibold uppercase text-n-slate-10">
                  Primario
                </p>
                <p class="mt-1 break-words text-sm font-semibold">
                  {{ selectedDuplicate.primaryEntity.label }}
                </p>
                <p class="mt-1 break-words text-xs text-n-slate-11">
                  {{ selectedDuplicate.primaryEntity.subtitle || '-' }}
                </p>
              </div>
              <div
                v-if="selectedDuplicate"
                class="rounded-lg border border-n-weak p-3"
              >
                <p class="text-xs font-semibold uppercase text-n-slate-10">
                  Duplicado
                </p>
                <p class="mt-1 break-words text-sm font-semibold">
                  {{ selectedDuplicate.duplicateEntity.label }}
                </p>
                <p class="mt-1 break-words text-xs text-n-slate-11">
                  {{ selectedDuplicate.duplicateEntity.subtitle || '-' }}
                </p>
              </div>
            </div>

            <div v-if="selectedIssue" class="space-y-4 py-4">
              <section>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Entidade e evidencia
                </h3>
                <p class="mt-1 break-words text-sm text-n-slate-11">
                  {{ selectedIssue.entity.label }}
                </p>
                <dl class="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                  <div
                    v-for="entry in entriesForDisplay(selectedIssue.evidence)"
                    :key="entry.key"
                    class="rounded-lg border border-n-weak p-2"
                  >
                    <dt class="font-semibold text-n-slate-11">
                      {{ humanize(entry.key) }}
                    </dt>
                    <dd class="mt-1 break-words text-n-slate-12">
                      {{ entry.value }}
                    </dd>
                  </div>
                </dl>
              </section>

              <section v-if="selectedIssue.relatedFraudSignals.length">
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Sinais de fraude relacionados
                </h3>
                <div class="mt-2 flex flex-col gap-2">
                  <div
                    v-for="signal in selectedIssue.relatedFraudSignals"
                    :key="signal.id"
                    class="rounded-lg border border-n-weak p-2 text-xs"
                  >
                    {{ humanize(signal.signalType) }} ·
                    {{ humanize(signal.disposition) }}
                  </div>
                </div>
              </section>
            </div>

            <div v-if="selectedDuplicate" class="space-y-4 py-4">
              <section class="grid gap-2 text-xs sm:grid-cols-3">
                <div class="rounded-lg border border-n-weak p-3">
                  <p class="font-semibold text-n-slate-10">Score do match</p>
                  <p class="mt-1 text-sm font-semibold text-n-slate-12">
                    {{ selectedDuplicate.matchScoreLabel }}
                  </p>
                </div>
                <div class="rounded-lg border border-n-weak p-3">
                  <p class="font-semibold text-n-slate-10">Tipo</p>
                  <p
                    class="mt-1 break-words text-sm font-semibold text-n-slate-12"
                  >
                    {{ humanize(selectedDuplicate.matchType) }}
                  </p>
                </div>
                <div class="rounded-lg border border-n-weak p-3">
                  <p class="font-semibold text-n-slate-10">Detector</p>
                  <p
                    class="mt-1 break-words text-sm font-semibold text-n-slate-12"
                  >
                    {{ selectedDuplicate.detectorKey || '-' }}
                  </p>
                </div>
              </section>

              <section>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Sinais e discriminadores
                </h3>
                <div class="mt-2 grid gap-2 sm:grid-cols-2">
                  <div class="rounded-lg border border-n-weak p-3">
                    <p class="text-xs font-semibold text-n-slate-10">Sinais</p>
                    <dl class="mt-2 space-y-1 text-xs">
                      <div
                        v-for="entry in entriesForDisplay(
                          selectedDuplicate.signals
                        )"
                        :key="entry.key"
                      >
                        <dt class="font-semibold text-n-slate-11">
                          {{ humanize(entry.key) }}
                        </dt>
                        <dd class="break-words text-n-slate-12">
                          {{ entry.value }}
                        </dd>
                      </div>
                    </dl>
                  </div>
                  <div class="rounded-lg border border-n-weak p-3">
                    <p class="text-xs font-semibold text-n-slate-10">
                      Discriminadores
                    </p>
                    <dl class="mt-2 space-y-1 text-xs">
                      <div
                        v-for="entry in entriesForDisplay(
                          selectedDuplicate.discriminators
                        )"
                        :key="entry.key"
                      >
                        <dt class="font-semibold text-n-slate-11">
                          {{ humanize(entry.key) }}
                        </dt>
                        <dd class="break-words text-n-slate-12">
                          {{ entry.value }}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </section>

              <section
                v-if="activeOperation"
                class="rounded-lg border border-n-weak p-3"
              >
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Operacao de merge
                </h3>
                <p class="mt-1 text-xs text-n-slate-11">
                  Status {{ humanize(activeOperation.status) }} · reversivel
                  {{ activeOperation.reversible ? 'sim' : 'nao' }}
                </p>
                <p class="mt-2 text-xs text-n-slate-11">
                  Referencias afetadas:
                  {{ activeOperation.preview.affected_reference_total || 0 }}
                </p>
              </section>
            </div>

            <div v-if="selectedFraud" class="space-y-4 py-4">
              <section>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Scores do sinal
                </h3>
                <dl class="mt-2 grid gap-2 sm:grid-cols-2">
                  <div
                    v-for="entry in entriesForDisplay(selectedFraud.scores)"
                    :key="entry.key"
                    class="rounded-lg border border-n-weak p-2 text-xs"
                  >
                    <dt class="font-semibold text-n-slate-11">
                      {{ humanize(entry.key) }}
                    </dt>
                    <dd>{{ entry.value }}</dd>
                  </div>
                </dl>
              </section>
            </div>

            <div v-if="selectedScore" class="space-y-4 py-4">
              <section>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Dimensoes do score
                </h3>
                <dl class="mt-2 grid gap-2 sm:grid-cols-2">
                  <div
                    v-for="entry in entriesForDisplay(selectedScore.dimensions)"
                    :key="entry.key"
                    class="rounded-lg border border-n-weak p-2 text-xs"
                  >
                    <dt class="font-semibold text-n-slate-11">
                      {{ humanize(entry.key) }}
                    </dt>
                    <dd>{{ entry.value }}</dd>
                  </div>
                </dl>
              </section>
            </div>

            <section
              v-if="selectedItem.linkedAlerts?.length"
              class="border-t border-n-weak py-4"
            >
              <h3 class="text-sm font-semibold text-n-slate-12">
                Alertas vinculados
              </h3>
              <div class="mt-2 flex flex-col gap-2">
                <div
                  v-for="alert in selectedItem.linkedAlerts"
                  :key="alert.id"
                  class="rounded-lg border border-n-weak p-2 text-xs"
                >
                  <p class="font-semibold text-n-slate-12">
                    {{ alert.title }}
                  </p>
                  <p class="mt-1 text-n-slate-11">
                    Cliente #{{ alert.contactId }} ·
                    {{ humanize(alert.status) }}
                  </p>
                </div>
              </div>
            </section>

            <section class="border-t border-n-weak pt-4">
              <label class="flex flex-col text-xs text-n-slate-11">
                Motivo da decisao humana
                <textarea
                  v-model="reviewReason"
                  rows="3"
                  class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
                  placeholder="Ex.: documento conferido, falso positivo, unidade diferente"
                  :disabled="!canManageGovernance"
                />
              </label>
              <p v-if="reasonMissing" class="mt-2 text-xs text-n-amber-11">
                Merge, rejeicao, reversao, resolucao e fraude exigem motivo.
              </p>

              <div
                v-if="selectedIssue"
                class="mt-3 grid gap-2 sm:grid-cols-2"
                data-test="data-quality-issue-actions"
              >
                <LaisPrimaryButton
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-user-check"
                  :disabled="!canManageGovernance"
                  :is-loading="
                    actionKey === `issue:acknowledge:${selectedIssue.id}`
                  "
                  @click="transitionIssue('acknowledge')"
                >
                  Assumir
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  size="sm"
                  icon="i-lucide-check-circle"
                  :disabled="!canManageGovernance || reasonMissing"
                  :is-loading="
                    actionKey === `issue:resolve:${selectedIssue.id}`
                  "
                  @click="transitionIssue('resolve')"
                >
                  Resolver
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-ban"
                  :disabled="!canManageGovernance || reasonMissing"
                  :is-loading="
                    actionKey === `issue:dismiss:${selectedIssue.id}`
                  "
                  @click="transitionIssue('dismiss')"
                >
                  Descartar
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-rotate-ccw"
                  :disabled="!canManageGovernance || reasonMissing"
                  :is-loading="actionKey === `issue:reopen:${selectedIssue.id}`"
                  @click="transitionIssue('reopen')"
                >
                  Reabrir
                </LaisPrimaryButton>
              </div>

              <div
                v-if="selectedDuplicate"
                class="mt-3 grid gap-2 sm:grid-cols-2"
                data-test="data-quality-duplicate-actions"
              >
                <LaisPrimaryButton
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-list-checks"
                  :disabled="
                    !canManageGovernance ||
                    reasonMissing ||
                    !selectedDuplicate.availableActions.includes('plan_merge')
                  "
                  :is-loading="actionKey === `plan:${selectedDuplicate.id}`"
                  @click="planMerge()"
                >
                  Planejar merge
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  size="sm"
                  icon="i-lucide-git-merge"
                  :disabled="
                    !canManageGovernance ||
                    reasonMissing ||
                    !selectedDuplicate.availableActions.includes('plan_merge')
                  "
                  :is-loading="actionKey === `apply:${selectedDuplicate.id}`"
                  @click="applyMerge"
                >
                  Aplicar merge
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-rotate-ccw"
                  :disabled="
                    !canManageGovernance ||
                    reasonMissing ||
                    !selectedDuplicate.availableActions.includes('revert_merge')
                  "
                  :is-loading="
                    activeOperation &&
                    actionKey === `revert:${activeOperation.id}`
                  "
                  @click="revertMerge"
                >
                  Reverter
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  variant="ghost"
                  size="sm"
                  icon="i-lucide-x-circle"
                  :disabled="
                    !canManageGovernance ||
                    reasonMissing ||
                    !selectedDuplicate.availableActions.includes('reject')
                  "
                  :is-loading="actionKey === `reject:${selectedDuplicate.id}`"
                  @click="rejectDuplicate"
                >
                  Rejeitar
                </LaisPrimaryButton>
              </div>

              <div
                v-if="selectedFraud"
                class="mt-3 space-y-3"
                data-test="data-quality-fraud-actions"
              >
                <div class="grid gap-2 sm:grid-cols-2">
                  <label class="flex min-w-0 flex-col text-xs text-n-slate-11">
                    Resultado
                    <select
                      v-model="fraudOutcome"
                      class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
                    >
                      <option
                        v-for="option in FRAUD_OUTCOME_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </label>
                  <label class="flex min-w-0 flex-col text-xs text-n-slate-11">
                    Escopo da quarentena
                    <select
                      v-model="quarantineScope"
                      class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
                    >
                      <option
                        v-for="option in QUARANTINE_SCOPE_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </label>
                </div>
                <div class="grid gap-2 sm:grid-cols-3">
                  <LaisPrimaryButton
                    size="sm"
                    icon="i-lucide-user-check"
                    :disabled="!canManageGovernance || reasonMissing"
                    :is-loading="
                      actionKey === `fraud-review:${selectedFraud.id}`
                    "
                    @click="reviewFraud"
                  >
                    Revisar
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-shield-alert"
                    :disabled="!canManageGovernance || reasonMissing"
                    :is-loading="
                      actionKey === `fraud-quarantine:${selectedFraud.id}`
                    "
                    @click="quarantineFraud"
                  >
                    Quarentenar
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-shield-check"
                    :disabled="!canManageGovernance || reasonMissing"
                    :is-loading="
                      actionKey === `fraud-release:${selectedFraud.id}`
                    "
                    @click="releaseFraud"
                  >
                    Liberar
                  </LaisPrimaryButton>
                </div>
              </div>

              <p
                v-if="actionMessage"
                class="mt-3 rounded-lg border border-n-teal-7 bg-n-teal-3 px-3 py-2 text-sm text-n-teal-11"
                role="status"
              >
                {{ actionMessage }}
              </p>
              <p
                v-if="conflictMessage"
                class="mt-3 rounded-lg border border-n-ruby-7 bg-n-ruby-3 px-3 py-2 text-sm text-n-ruby-11"
                role="alert"
              >
                {{ conflictMessage }}
              </p>
            </section>
          </template>
        </LaisCard>
      </aside>
    </section>
  </main>
</template>
